/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { FieldValues, FormProviderProps } from "react-hook-form";
import { FormProvider, useWatch } from "react-hook-form";
import type { ZodObject, ZodRawShape } from "zod";

type FormWarningsContextValue = {
  warnings: Record<string, string>;
};

const FormWarningsContext = createContext<FormWarningsContextValue>({
  warnings: {},
});

export const useFormWarnings = () => useContext(FormWarningsContext);

type FormProps<
  TFieldValues extends FieldValues,
  TContext = any,
  TTransformedValues extends FieldValues | undefined = undefined,
> = {
  formCSS?: Interpolation;
  formProps: React.HTMLAttributes<HTMLFormElement>;
  warningSchema?: ZodObject<ZodRawShape>;
} & FormProviderProps<TFieldValues, TContext, TTransformedValues>;

export const Form = <
  TFieldValues extends FieldValues,
  TContext = any,
  TTransformedValues extends FieldValues | undefined = undefined,
>({
  formProps,
  formCSS,
  warningSchema,
  ...props
}: FormProps<TFieldValues, TContext, TTransformedValues>) => {
  const [warnings, setWarnings] = useState<Record<string, string>>({});

  const hasWarningSchema = useMemo(
    () => warningSchema && Object.keys(warningSchema.shape).length > 0,
    [warningSchema],
  );

  const values = useWatch({
    control: props.control,
    disabled: !hasWarningSchema,
  });

  useEffect(() => {
    if (!hasWarningSchema || !warningSchema) return;

    const result = warningSchema.safeParse(values);

    if (!result.success) {
      const newWarnings: Record<string, string> = {};
      result.error.issues.forEach((error) => {
        const fieldPath = error.path.join(".");
        newWarnings[fieldPath] = error.message;
      });
      setWarnings(newWarnings);
    } else {
      setWarnings({});
    }
  }, [values, warningSchema, hasWarningSchema]);

  return (
    <FormProvider {...props}>
      <FormWarningsContext.Provider value={{ warnings }}>
        <form css={formCSS} {...formProps}>
          {props.children}
        </form>
      </FormWarningsContext.Provider>
    </FormProvider>
  );
};

Form.displayName = "Form";
